/**
 * Combine all reducers in this file and export the combined reducers.
 * If we were to do this in store.js, reducers wouldn't be hot reloadable.
 */
import { combineReducers } from 'redux-immutable';
import { AnyAction, Reducer } from 'redux';
import { connectRouter } from 'connected-react-router/immutable';
import { reducer as formReducer } from 'redux-form/immutable';
import { RouterState } from 'connected-react-router';
import { FormStateMap } from 'redux-form';
import { fromJS } from 'immutable';

import entitiesReducer, { EntityState } from 'entities/reducer';
import notificationReducer from 'containers/Notification/reducer';
import localSettingsReducer from 'localSettings/reducer';
import history from 'utils/history';
import type { TypedMap } from 'utils/typed-map';
import type { AutoSourcingPageState } from 'containers/AutoSourcingPage/reducer';
import type { CampaignPageState } from 'containers/CampaignPage/reducer';
import type { DonationsSeedState } from 'containers/DonationsSeedPage/reducers';
import type { ExternalDonationRequestsState } from 'containers/ExternalDonationRequests/reducer';
import type { VolunteerFormPageState } from 'containers/VolunteerFormPage/reducer';
import type { CausesPageState } from 'containers/CausesPage/reducer';
import type { DeedFormState } from 'containers/DeedFormPage/reducer';
import type { DonationCreditRulesFormPageState } from 'containers/DonationCreditsRulesFormPage/reducer';
import type { DonationPageState } from 'containers/DonationPage/reducer';
import type { DonationRulesListPageState } from 'containers/DonationRulesListPage/reducers';
import type { PayrollApiPageState } from 'containers/PayrollApiPage/reducer';
import type { LocalSettingsState } from 'localSettings/reducer';

export type MutableState = {
  router: RouterState<unknown>;
  entities: EntityState;
  notification: Reducer;
  form: FormStateMap;
  localSettings: LocalSettingsState;
};

export type InjectedState = {
  autoSourcingPage?: AutoSourcingPageState;
  campaignPage?: CampaignPageState;
  causesPage?: CausesPageState;
  deedFormPage?: DeedFormState;
  donationCreditRulesFormPage?: DonationCreditRulesFormPageState;
  donationGivingRulesListPage?: DonationRulesListPageState;
  donationPage?: DonationPageState;
  donationsSeedPage?: DonationsSeedState;
  externalDonationRequests?: ExternalDonationRequestsState;
  payrollApiPage?: PayrollApiPageState;
  VolunteerFormPage: VolunteerFormPageState;
};
export type InjectedReducers = Reducer<InjectedState, AnyAction>;

export type State = TypedMap<MutableState & InjectedState>;

/**
 * Creates the main reducer with the asynchronously loaded ones
 */
export default (injectedReducers?: InjectedReducers): Reducer<MutableState> => {
  const appReducer = combineReducers({
    router: connectRouter(history),
    entities: entitiesReducer,
    notification: notificationReducer,
    form: formReducer,
    localSettings: localSettingsReducer,
    ...injectedReducers,
  });

  // Wrap the reducer to handle ImpactAI direct state updates
  return (state: any, action: AnyAction) => {
    // Handle direct state updates from ImpactAI Redux method
    if (action.type === '@@IMPACT_AI/DIRECT_STATE_UPDATE' && action.meta) {
      const { path, value } = action.meta;
      try {
        // Check if we need to create intermediate paths
        const currentPath = [];
        let workingState = state;

        // Iterate through the path to ensure all parent objects exist
        for (let i = 0; i < path.length - 1; i += 1) {
          currentPath.push(path[i]);
          const nextValue = workingState.get(path[i]);

          if (nextValue === undefined) {
            // Create an empty Map at this path
            workingState = workingState.setIn(currentPath, fromJS({}));
          }
        }

        // Now set the actual value
        const newState = workingState.setIn(path, value);
        return newState;
      } catch (error) {
        return state;
      }
    }

    // For all other actions, use the normal reducer
    return appReducer(state, action);
  };
};
